import React, { useState, useEffect } from 'react';
import { AppBar, Toolbar, Typography, Button, InputBase, useTheme, useMediaQuery, Avatar, Menu, MenuItem, IconButton, Badge } from '@mui/material';
import { styled, alpha } from '@mui/material/styles';
import SearchIcon from '@mui/icons-material/Search';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { logout, getHeaderState } from '../services/api';
import PopoverMenu from './common/PopoverMenu';
import { useUser } from '../contexts/UserContext';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import LogoutIcon from '@mui/icons-material/Logout';
import SettingsIcon from '@mui/icons-material/Settings';
import PublicIcon from '@mui/icons-material/Public';
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import GavelIcon from '@mui/icons-material/Gavel';
import SecurityIcon from '@mui/icons-material/Security';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import { isPwa } from '../utils/isPwa';
import NotificationsMenu from './common/NotificationsMenu';
import XIcon from '@mui/icons-material/X';
import BoltIcon from '@mui/icons-material/Bolt';
import MenuIcon from '@mui/icons-material/Menu';
import LibraryBooksIcon from '@mui/icons-material/LibraryBooks';
import RateReviewIcon from '@mui/icons-material/RateReview';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import ListIcon from '@mui/icons-material/List';
import HistoryIcon from '@mui/icons-material/History';
import PeopleIcon from '@mui/icons-material/People';
import SportsEsportsIcon from '@mui/icons-material/SportsEsports';
import PersonIcon from '@mui/icons-material/Person';
import CameraIcon from '@mui/icons-material/Camera';
import NewReleasesIcon from '@mui/icons-material/NewReleases';
import HubIcon from '@mui/icons-material/Hub';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import { ReleaseNote } from '../types/ReleaseNote';
import { isEarlyAccess } from '../utils/subscription';
import FeatureAnnouncementDialog from './dialogs/FeatureAnnouncementDialog';

const Search = styled('div')(({ theme }) => ({
  position: 'relative',
  borderRadius: theme.shape.borderRadius,
  backgroundColor: alpha(theme.palette.common.white, 0.15),
  '&:hover': {
    backgroundColor: alpha(theme.palette.common.white, 0.25),
  },
  marginRight: theme.spacing(2),
  marginLeft: 0,
  width: '100%',
  [theme.breakpoints.up('sm')]: {
    marginLeft: theme.spacing(3),
    width: 'auto',
  },
  [theme.breakpoints.down('sm')]: {
    display: 'none', // スマートフォンでは非表示
  },
}));

const SearchIconWrapper = styled('div')(({ theme }) => ({
  padding: theme.spacing(0, 2),
  height: '100%',
  position: 'absolute',
  pointerEvents: 'none',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}));

const StyledInputBase = styled(InputBase)(({ theme }) => ({
  color: 'inherit',
  '& .MuiInputBase-input': {
    padding: theme.spacing(1, 1, 1, 0),
    paddingLeft: `calc(1em + ${theme.spacing(4)})`,
    transition: theme.transitions.create('width'),
    width: '100%',
    [theme.breakpoints.up('md')]: {
      width: '20ch',
    },
  },
}));

const Header: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const { user, isAuthenticated, isLoading, clearUser } = useUser();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [anchorEl, setAnchorEl] = useState< null | HTMLElement >(null);
  const open = Boolean(anchorEl);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();
  const isTransparent = location.pathname === '/' || location.pathname.startsWith('/game/') || location.pathname.startsWith('/anime/') || location.pathname.startsWith('/goty/') || location.pathname.startsWith('/mygoty/');
  const [navAnchorEl, setNavAnchorEl] = useState<null | HTMLElement>(null);
  const navOpen = Boolean(navAnchorEl);

  // ヘッダーUI状態（リリースノートのバッジ/ポップアップ用）
  // 【設計方針】今後お知らせ機能を追加する場合も headerState に統合する。
  // latest_announcement, latest_popup_announcement を追加し、同じパターンでバッジ/ポップアップを制御する。
  const [headerState, setHeaderState] = useState<{
    latest_release_note: ReleaseNote | null;
    latest_popup_release_note: ReleaseNote | null;
    // TODO: お知らせ機能追加時
    // latest_announcement: Announcement | null;
    // latest_popup_announcement: Announcement | null;
  } | null>(null);

  useEffect(() => {
    if (isAuthenticated) {
      getHeaderState().then(setHeaderState);
    }
  }, [isAuthenticated]);

  // リリースノートの未読判定（バッジ表示用）
  // localStorage の 'release-note-seen-id' に最後に確認したリリースノートIDを保存
  // EA限定のリリースノートはEAユーザーにのみバッジ表示
  const [seenReleaseNoteId, setSeenReleaseNoteId] = useState(() => localStorage.getItem('release-note-seen-id'));

  // 初回ログイン時（seenReleaseNoteIdがnull）は最新IDを既読として保存し、バッジを出さない
  useEffect(() => {
    if (seenReleaseNoteId === null && headerState?.latest_release_note) {
      const id = String(headerState.latest_release_note.id);
      localStorage.setItem('release-note-seen-id', id);
      setSeenReleaseNoteId(id);
    }
  }, [seenReleaseNoteId, headerState]);

  const hasUnreadReleaseNote = !!(
    headerState?.latest_release_note
    && seenReleaseNoteId !== null
    && seenReleaseNoteId !== String(headerState.latest_release_note.id)
    && (headerState.latest_release_note.category !== 2 || isEarlyAccess(user?.subscription_plan ?? 0))
  );

  // ポップアップ表示判定（is_popup=true のリリースノートのみ、EA制御あり）
  const popupReleaseNote = headerState?.latest_popup_release_note;
  const shouldShowPopup = !!(
    popupReleaseNote
    && (popupReleaseNote.category !== 2 || isEarlyAccess(user?.subscription_plan ?? 0))
  );

  useEffect(() => {
    // メニューボタンのイベントをリスン
    const handleToggleNavMenu = (event: Event) => {
      const customEvent = event as CustomEvent;
      if (customEvent.detail) {
        const { anchorEl, rect } = customEvent.detail;
        // メニューの位置を設定（ボタンの上に表示）
        setNavAnchorEl(prev => prev ? null : anchorEl);
      }
    };
    window.addEventListener('toggleNavMenu', handleToggleNavMenu);

    const handleScroll = () => {
      const scrollPosition = window.scrollY;
      setIsScrolled(scrollPosition > 50);
    };

    if (isTransparent) {
      window.addEventListener('scroll', handleScroll);
    }

    return () => {
      window.removeEventListener('toggleNavMenu', handleToggleNavMenu);
      if (isTransparent) {
        window.removeEventListener('scroll', handleScroll);
      }
    };
  }, [isTransparent, navAnchorEl]);

  // プロフィールページのメディアタブに応じて検索対象を切り替え
  const mediaParam = location.pathname.startsWith('/profile')
    ? new URLSearchParams(location.search).get('media')
    : null;
  const searchMediaMode = mediaParam === 'anime' ? 'anime' : 'game';
  const searchPlaceholder = searchMediaMode === 'anime' ? 'アニメを検索...' : 'ゲームを検索...';

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  const handleSearchSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const q = encodeURIComponent(searchTerm.trim());
    const modeParam = searchMediaMode !== 'game' ? `&mode=${searchMediaMode}` : '';
    navigate(`/search?q=${q}${modeParam}`);
  };

  const handleLogout = async () => {
    await logout();
    clearUser();
    navigate('/');
  };

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleNavClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setNavAnchorEl(event.currentTarget);
  };

  const handleNavClose = () => {
    setNavAnchorEl(null);
  };

  const menuOptions = [
    {
      icon: <AccountCircleIcon />,
      label: 'マイページ',
      onClick: () => {
        navigate('/profile');
        handleClose();
      }
    },
    {
      icon: <SettingsIcon />,
      label: '設定',
      onClick: () => {
        navigate('/profile/settings');
        handleClose();
      }
    },
    {
      icon: <PublicIcon />,
      label: 'グローバルTL',
      onClick: () => {
        navigate('/public-timeline');
        handleClose();
      }
    },
    // Hub はスーパーユーザー限定（一般公開時に解除）
    ...(user?.is_superuser ? [{
      icon: <HubIcon />,
      label: 'PULSE Lab',
      onClick: () => {
        navigate('/hub');
        handleClose();
      }
    }] : []),
    // PWAでない場合のみインストールオプションを表示
    ...(!isPwa() ? [{
      icon: <PhoneIphoneIcon />,
      label: 'インストール',
      onClick: () => {
        navigate('/install-pwa');
        handleClose();
      }
    }] : []),
    {
      icon: <XIcon />,
      label: '最新情報',
      onClick: () => {
        window.open('https://x.com/pulse_database', '_blank');
        handleClose();
      }
    },
    {
      icon: hasUnreadReleaseNote
        ? <Badge variant="dot" color="primary"><NewReleasesIcon /></Badge>
        : <NewReleasesIcon />,
      label: 'リリースノート',
      onClick: () => {
        // 既読にする（バッジを消す）
        if (headerState?.latest_release_note) {
          const id = String(headerState.latest_release_note.id);
          localStorage.setItem('release-note-seen-id', id);
          setSeenReleaseNoteId(id);
        }
        navigate('/release-notes');
        handleClose();
      }
    },
    {
      icon: <HelpOutlineIcon />,
      label: 'ヘルプ・サポート',
      items: [
        {
          icon: <EmailOutlinedIcon />,
          label: 'お問い合わせ',
          onClick: () => {
            window.open('https://dev.plsdb.com/contact', '_blank');
            handleClose();
          }
        },
        {
          icon: <GavelIcon />,
          label: '利用規約',
          onClick: () => {
            navigate('/terms-of-service');
            handleClose();
          }
        },
        {
          icon: <SecurityIcon />,
          label: 'プライバシー',
          onClick: () => {
            navigate('/privacy-policy');
            handleClose();
          }
        }
      ]
    },
    // 管理者メニュー
    ...(user?.is_superuser ? [{
      icon: <AdminPanelSettingsIcon />,
      label: '管理パネル',
      items: [
        {
          icon: <SportsEsportsIcon />,
          label: 'ゲーム管理',
          onClick: () => {
            navigate('/admin-panel');
            handleClose();
          }
        },
        {
          icon: <HubIcon />,
          label: 'Hub管理',
          onClick: () => {
            navigate('/admin-panel/hub');
            handleClose();
          }
        },
      ]
    }] : []),    
    {
      icon: <LogoutIcon />,
      label: 'ログアウト',
      onClick: () => {
        handleLogout();
        handleClose();
      }
    }
  ];

  const navMenuOptions = [
    {
      icon: <BoltIcon sx={{ transform: 'rotate(20deg)' }} />,
      label: 'タイムライン',
      onClick: () => {
        navigate('/profile?tab=timeline');
        handleNavClose();
      }
    },
    {
      icon: <PersonIcon />,
      label: 'プロフィール',
      onClick: () => {
        navigate('/profile?tab=profile');
        handleNavClose();
      }
    },
    {
      icon: <SportsEsportsIcon />,
      label: 'ライブラリ',
      onClick: () => {
        navigate('/profile?tab=library');
        handleNavClose();
      }
    },
    {
      icon: <RateReviewIcon />,
      label: 'レビュー',
      onClick: () => {
        navigate('/profile?tab=reviews');
        handleNavClose();
      }
    },
    {
      icon: <CameraIcon />,
      label: 'メモリー',
      onClick: () => {
        navigate('/profile?tab=memories');
        handleNavClose();
      }
    },
    {
      icon: <PlayArrowIcon />,
      label: 'プレイログ',
      onClick: () => {
        navigate('/profile?tab=playlogs');
        handleNavClose();
      }
    },
    {
      icon: <ListIcon />,
      label: 'リスト',
      onClick: () => {
        navigate('/profile?tab=lists');
        handleNavClose();
      }
    },
    {
      icon: <HistoryIcon />,
      label: 'アクティビティ',
      onClick: () => {
        navigate('/profile?tab=activity');
        handleNavClose();
      }
    },
    {
      icon: <PeopleIcon />,
      label: 'フォロー',
      onClick: () => {
        navigate('/profile?tab=follows');
        handleNavClose();
      }
    }
  ];

  return (
    <AppBar 
      position="fixed"
      sx={{
        background: isTransparent
          ? isScrolled 
            ? 'rgba(18, 18, 18, 0.96)'
            : 'transparent'
          : 'rgba(18, 18, 18, 0.96)',
        boxShadow: isTransparent && !isScrolled ? 'none' : undefined,
        transition: 'background-color 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
        backdropFilter: isScrolled || !isTransparent ? 'blur(10px)' : 'none',
      }}
    >
      <Toolbar sx={{
        px: 1.5,
        // maxWidth: '1140px',
        // width: '100%',
        // margin: '0 auto'
      }}>
        <Typography variant="h6" component="div" sx={{ flexGrow: 1, display: 'flex', alignItems: 'center' }}>
          <Link to="/" style={{ color: 'inherit', textDecoration: 'none', display: 'flex', alignItems: 'center' }}>
            <img
              src="/pulse_logo.png"
              alt="PULSE Logo"
              style={{
                height: isMobile ? '22px' : '28px',
                marginRight: '8px'
              }}
            />
          </Link>
        </Typography>
        <Search>
          <SearchIconWrapper>
            <SearchIcon />
          </SearchIconWrapper>
          <form onSubmit={handleSearchSubmit}>
            <StyledInputBase
              placeholder={searchPlaceholder}
              inputProps={{ 'aria-label': 'search' }}
              value={searchTerm}
              onChange={handleSearchChange}
            />
          </form>
        </Search>
        {isMobile && !isPwa() && (
          <IconButton
            color="inherit"
            aria-label="search"
            onClick={() => navigate(searchMediaMode !== 'game' ? `/search?mode=${searchMediaMode}` : '/search')}
            sx={{ mr: isMobile ? 0 : 1 }}
            size="medium"
          >
            <SearchIcon />
          </IconButton>
        )}
        {isAuthenticated ? (
          <>
            {isMobile && !isPwa() && (
              <IconButton
                onClick={handleNavClick}
                size="medium"
                sx={{
                  backgroundColor: navOpen ? 'rgba(255, 255, 255, 0.08)' : 'transparent',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.12)'
                  }
                }}
              >
                <MenuIcon  />
              </IconButton>
            )}
            <NotificationsMenu />
            <IconButton
              onClick={handleClick}
              size="medium"
              sx={{ ml: isMobile ? 0 : 1 }}
            >
              <Badge
                variant="dot"
                color="primary"
                invisible={!hasUnreadReleaseNote}
                sx={{
                  '& .MuiBadge-badge': {
                    top: 2,
                    right: 2,
                    width: 8,
                    height: 8,
                    minWidth: 8,
                    borderRadius: '50%',
                  }
                }}
              >
                <Avatar alt={user?.display_name} sx={{ width: 32, height: 32, backgroundColor: 'rgba(255, 255, 255, 0.3)' }} />
              </Badge>
            </IconButton>
            <PopoverMenu
              open={open}
              anchorEl={anchorEl}
              onClose={handleClose}
              options={menuOptions}
            />
            <PopoverMenu
              open={navOpen}
              anchorEl={navAnchorEl}
              onClose={handleNavClose}
              options={navMenuOptions}
              anchorOrigin={isPwa() ? {
                vertical: 'top',
                horizontal: 'center',
              } : {
                vertical: 'bottom',
                horizontal: 'right',
              }}
              transformOrigin={isPwa() ? {
                vertical: 'bottom',
                horizontal: 'center',
              } : {
                vertical: 'top',
                horizontal: 'right',
              }}
              PaperProps={isPwa() ? {
                sx: {
                  marginBottom: '8px',
                  maxHeight: '80vh',
                  width: 'auto',
                  maxWidth: '360px'
                }
              } : undefined}
            />
          </>
        ) : !isLoading ? (
          <Button color="inherit" component={Link} to="/login">ログイン</Button>
        ) : null}
      </Toolbar>

      {/* リリースノートのポップアップダイアログ（is_popup=true のもののみ） */}
      {isAuthenticated && shouldShowPopup && popupReleaseNote && (
        <FeatureAnnouncementDialog
          announcement={{
            id: `release-note-${popupReleaseNote.id}`,
            title: popupReleaseNote.title || 'Update',
            description: popupReleaseNote.description,
            linkText: popupReleaseNote.link_text,
            linkUrl: popupReleaseNote.link_url,
            badge: popupReleaseNote.category === 2 ? 'Early Access' : 'NEW',
            badgeColor: popupReleaseNote.category === 2 ? 'warning' : 'primary',
            version: popupReleaseNote.version,
          }}
          isEarlyAccess={popupReleaseNote.category === 2}
          storageKey="latest-release-note-dismissed"
          onClose={() => {
            // ポップアップを閉じたらアバターのバッジも既読にする
            if (headerState?.latest_release_note) {
              const id = String(headerState.latest_release_note.id);
              localStorage.setItem('release-note-seen-id', id);
              setSeenReleaseNoteId(id);
            }
          }}
          onNavigate={popupReleaseNote.link_url ? () => {
            if (popupReleaseNote.link_url!.startsWith('http')) {
              window.open(popupReleaseNote.link_url, '_blank', 'noopener noreferrer');
            } else {
              navigate(popupReleaseNote.link_url!);
            }
          } : undefined}
        />
      )}
    </AppBar>
  );
};
export default Header;
